import type { FieldConfiguration, ListBuilderColumn } from '../types';

/**
 * The one purchase-order column set, shared by every purchase-order list —
 * the main list, the vendor's Purchase Orders tab and the item's Purchase
 * Orders tab (PD-15581). Keeping a single builder is what stops the three
 * lists drifting apart again: before this they were three hand-copied column
 * arrays that had already diverged (the vendor tab had lost the status QUOTE
 * colour rule, the main list had grown an ID field on the same line as the
 * PO number).
 *
 * Layout decisions, all from PD-15581's review of the list against the rest
 * of the application:
 *  - PO Number / ID stack on two lines instead of sharing one.
 *  - Order Date / Requested Date stack, as do Items / Total, so the row takes
 *    less horizontal space.
 *  - Every line of a stacked cell is `sm`; only the colour separates the
 *    secondary line from the primary one. The item and client lists read
 *    that way, and `xs` (0.625rem vs 0.75rem) made these rows look shrunken
 *    next to them.
 *  - Column ids are kept from the previous configuration (`po-number`,
 *    `status`, `order-date`, `item-count`, `actions`) so saved searches and
 *    default-sort ids that reference them keep resolving.
 */
export interface PurchaseOrderColumnOptions {
	/** Vendor column — off on the vendor's own tab, where every row is that vendor. */
	includeVendor?: boolean;
	/** Follow-Up date — only the main list's quote tabs sort on it. */
	includeFollowUp?: boolean;
	/** Row-action dropdown (Edit / Delete) — the main list only. */
	includeActions?: boolean;
	/**
	 * The requesting item's own figures on each PO — ordered / received and its
	 * expected date. Only the item's Purchase Orders tab, whose query is scoped
	 * to one item, returns these fields.
	 */
	includeItemLine?: boolean;
}

/** Badge colour per status code; mirrors `PurchaseOrderStatusLabels`' codes. */
const STATUS_COLORS: ReadonlyArray<
	[string, 'neutral' | 'info' | 'success' | 'warning' | 'danger']
> = [
	['0', 'neutral'], // New
	['1', 'info'], // Pending Approval
	['2', 'success'], // Approved
	['3', 'info'], // Issued
	['4', 'warning'], // Partially Received
	['5', 'success'], // Received
	['6', 'warning'], // Held
	['7', 'neutral'], // Cancelled
	['8', 'warning'], // Backordered
	['9', 'danger'], // Rejected
	['10', 'info'], // Quote
];

/** Empty line-break field that stacks the next field on its own line. */
const lineBreak = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: {
		size: 'md',
		colorRule: { type: 'theme', themeColor: 'textSecondary' },
	},
	displayStyle: 'line_break',
});

const poNumberColumn = (): ListBuilderColumn => ({
	id: 'po-number',
	fields: [
		{
			id: 'po-number-field',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'purchase_order',
				idField: '_id',
			},
			dataConfig: { type: 'field', field: { fieldName: 'externalId' } },
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('po-number-lb'),
		{
			id: 'po-id-field',
			dataConfig: { type: 'field', field: { fieldName: '_id' } },
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h1', text: 'PO Number' },
		{ id: 'h1-sub', text: 'ID' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'externalId' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

const vendorColumn = (): ListBuilderColumn => ({
	id: 'po-vendor',
	fields: [
		{
			id: 'po-vendor-field',
			// PD-14564: link to the vendor detail. The payload exposes a flat
			// `vendorId` (PD-14568), so the object link resolves like
			// call_history's vendor link.
			link: {
				type: 'object',
				enabled: true,
				idField: 'vendorId',
				objectTable: 'vendor',
			},
			dataConfig: {
				type: 'field',
				// PD-14568 attaches a nested `vendor` object to the payload; the
				// renderer reads dotted paths (getNestedFieldValue).
				field: { fieldName: 'vendor.name' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-vendor', text: 'Vendor' }],
	minWidth: 140,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

const statusColumn = (): ListBuilderColumn => ({
	id: 'status',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'statusLabel' },
				defaultValue: 'Unknown',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'conditional',
					rules: STATUS_COLORS.map(([value, color]) => ({
						field: { fieldName: 'status' },
						operator: 'equals' as const,
						value,
						color: { type: color },
					})),
					defaultColor: { type: 'neutral' },
				},
			},
			displayStyle: 'badge',
		},
	],
	headers: [{ id: 'h2', text: 'Status' }],
	minWidth: 140,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'status' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

const datesColumn = (): ListBuilderColumn => ({
	id: 'order-date',
	fields: [
		{
			id: 'order-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'purchase_order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'date',
		},
		lineBreak('order-date-lb'),
		{
			id: 'requested-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'requestedDate' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{ id: 'h4', text: 'Order Date' },
		{ id: 'h3', text: 'Requested' },
	],
	minWidth: 110,
	sortable: true,
	alignment: 'center',
	sortFields: [
		{ direction: 'DESC', fieldName: 'date', objectTable: 'purchase_order' },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'middle',
});

const followUpColumn = (): ListBuilderColumn => ({
	// Follow-up date — when the buyer planned to chase the vendor for a quote
	// response. Blank for POs without one (most non-Quote POs). The Quotes —
	// Follow-Up list sorts on it so the longest-pending follow-ups surface first.
	id: 'follow-up-date',
	fields: [
		{
			id: 'follow-up-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'followUpDate', objectTable: 'purchase_order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'date',
		},
	],
	headers: [{ id: 'h-follow-up', text: 'Follow-Up' }],
	minWidth: 110,
	sortable: true,
	alignment: 'center',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'followUpDate',
			objectTable: 'purchase_order',
		},
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'middle',
});

const itemsTotalColumn = (): ListBuilderColumn => ({
	id: 'item-count',
	fields: [
		{
			id: 'item-count-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemCount' },
				defaultValue: '0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('item-count-lb'),
		{
			id: 'total-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'total' },
				defaultValue: '0.00',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h5', text: 'Items' },
		{ id: 'h6', text: 'Total' },
	],
	minWidth: 100,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'itemCount' }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'middle',
});

/**
 * The requesting item's own lines on the PO, as returned by the list query
 * when it is scoped to an item (`itemQuantityOrdered` / `itemQuantityReceived`
 * / `itemExpectedReceiptDate`). Two columns: quantities stacked, then the
 * expected date.
 */
const itemLineColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'item-line-quantities',
		fields: [
			{
				id: 'item-quantity-ordered-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'itemQuantityOrdered' },
					defaultValue: '-',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { numberFormat: 'auto' },
				},
				displayStyle: 'number',
			},
			lineBreak('item-line-quantities-lb'),
			{
				id: 'item-quantity-received-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'itemQuantityReceived' },
					defaultValue: '-',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { numberFormat: 'auto' },
				},
				displayStyle: 'number',
			},
		],
		headers: [
			{ id: 'h-item-ordered', text: 'Item Ordered' },
			{ id: 'h-item-received', text: 'Item Received' },
		],
		minWidth: 110,
		sortable: false,
		alignment: 'right',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'middle',
	},
	{
		id: 'item-line-expected-date',
		fields: [
			{
				id: 'item-expected-date-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'itemExpectedReceiptDate' },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-item-expected', text: 'Item Expected' }],
		minWidth: 110,
		sortable: false,
		alignment: 'center',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'middle',
	},
];

const actionsColumn = (): ListBuilderColumn => ({
	id: 'actions',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'actions_dropdown',
			// PD-15581: each action asks the page whether THIS row may be edited /
			// deleted (imported purchase orders may not), on top of the RBAC gate
			// the page applies by wiring or withholding the `edit` / `delete`
			// callbacks themselves.
			dropdownActions: [
				{
					id: 'edit-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'edit',
					visibilityCallback: 'canEditPurchaseOrder',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'delete',
					visibilityCallback: 'canDeletePurchaseOrder',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [{ id: 'actions-header', text: '' }],
	width: 80,
	align: 'center',
	sortable: false,
});

export function buildPurchaseOrderColumns(
	options: PurchaseOrderColumnOptions = {},
): Array<ListBuilderColumn> {
	const {
		includeVendor = true,
		includeFollowUp = false,
		includeActions = false,
		includeItemLine = false,
	} = options;

	return [
		poNumberColumn(),
		...(includeVendor ? [vendorColumn()] : []),
		statusColumn(),
		datesColumn(),
		...(includeFollowUp ? [followUpColumn()] : []),
		...(includeItemLine ? itemLineColumns() : []),
		itemsTotalColumn(),
		...(includeActions ? [actionsColumn()] : []),
	];
}

/**
 * The fields every purchase-order list needs the query to return, whatever
 * columns a saved search keeps.
 */
export const PURCHASE_ORDER_REQUIRED_FIELDS: Array<string> = [
	'purchaseOrder.id',
	'purchaseOrder.externalId',
	'purchaseOrder.status',
	'purchaseOrder.date',
	'purchaseOrder.requestedDate',
	'purchaseOrder.vendorId',
];
